import {
  RichTableBlockModel,
  RichTableCellBlockModel,
  RichTableRowBlockModel,
} from '@blocksuite/affine-model';
import { matchModels } from '@blocksuite/affine-shared/utils';
import type { BlockModel, Store } from '@blocksuite/store';

/** Walks up from `model` to the `affine:rich-table` containing it, if any. */
export function findRichTable(
  model: BlockModel | null | undefined
): RichTableBlockModel | null {
  let current: BlockModel | null = model ?? null;
  while (current) {
    if (matchModels(current, [RichTableBlockModel])) return current;
    current = current.parent;
  }
  return null;
}

/** Walks up from `model` to the `affine:rich-table-cell` containing it. */
export function findRichTableCell(
  model: BlockModel | null | undefined
): RichTableCellBlockModel | null {
  let current: BlockModel | null = model ?? null;
  while (current) {
    if (matchModels(current, [RichTableCellBlockModel])) return current;
    current = current.parent;
  }
  return null;
}

export function tableRows(table: RichTableBlockModel) {
  return table.children.filter(child =>
    matchModels(child, [RichTableRowBlockModel])
  );
}

export function rowCells(row: BlockModel) {
  return row.children.filter(child =>
    matchModels(child, [RichTableCellBlockModel])
  );
}

/**
 * Restores the invariants a rich table must hold after any mutation:
 *
 * - every row holds exactly `columnWidths.length` cells;
 * - every cell holds at least one block, or there is nothing to click into;
 * - a table with no rows or no columns is removed.
 *
 * Every structural path — insert, delete, drag out, paste — has to run this.
 * It is safe to call twice: a table removed by an earlier pass is skipped.
 *
 * @returns true if the table was removed.
 */
export function normalizeRichTable(
  store: Store,
  table: RichTableBlockModel | null | undefined
): boolean {
  if (!table || store.readonly) return false;
  // A table deleted by an earlier normalize pass is no longer reachable.
  if (!store.getBlock(table.id)) return false;

  const rows = tableRows(table);
  const columnCount = table.props.columnWidths.length;

  if (rows.length === 0 || columnCount === 0) {
    store.deleteBlock(table);
    return true;
  }

  for (const row of rows) {
    const cells = rowCells(row);

    // Trim extras from the right, then pad the shortfall.
    cells.slice(columnCount).forEach(cell => store.deleteBlock(cell));
    for (let i = cells.length; i < columnCount; i++) {
      store.addBlock('affine:rich-table-cell', {}, row.id);
    }

    for (const cell of rowCells(row)) {
      if (cell.children.length === 0) {
        store.addBlock('affine:paragraph', {}, cell.id);
      }
    }
  }

  return false;
}
